import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ProductCentreSearchService } from '@app/core/services/search/product-centre-search.service';
import { BusinessEntitiesLogicService } from '@app/logic/business-entity';
import {
    IActiveIdAndNameDto,
    IProductCentreDocumentDto,
    IProductCentreTierDocumentDto,
    PRODUCT_TIER_RANGE_ENUM,
    ProductTierRangeEnumId
} from '@classictechsolutions/hubapi-transpiled-enums';
import { take } from 'rxjs';
import { getBaseFormComponentDirectiveProvider } from '../../../base-form-component';
import {
    AutocompleteScrollerComponent,
    getBaseAutocompleteScrollerProvider
} from '../../autocomplete-scroller.component';

@Component({
    selector: 'cb-product-centre-autocomplete-scroller',
    templateUrl: 'product-centre-autocomplete-scroller.component.html',
    styleUrls: ['../../autocomplete-scroller.component.scss'],
    providers: [
        ...getBaseFormComponentDirectiveProvider(ProductCentreAutocompleteScrollerComponent),
        getBaseAutocompleteScrollerProvider(ProductCentreAutocompleteScrollerComponent),
        ProductCentreSearchService,
    ],
    standalone: false
})
export class ProductCentreAutocompleteScrollerComponent
    extends AutocompleteScrollerComponent<IProductCentreDocumentDto>
    implements OnInit
{
    @Input() public readonly displayCategories: boolean;
    @Input() public readonly productType: number;
    @Input() public readonly hideCompositeItems: boolean;
    @Input() public readonly includeCategories: boolean;
    @Input() public readonly orderByStandardProduct: boolean;
    @Input() public readonly businessEntityIds: Set<number> = new Set();
    @Input() public readonly ignoreTierBasedSorting = false;

    private _categoryId: number;
    public get categoryId(): number {
        return this._categoryId;
    }

    @Input() public set categoryId(id: number) {
        this._categoryId = id;
        this.searchService.categoryIds = [id];
        this.reperformSearch();
    }

    private readonly separator = ' - ';
    public searchResultsLoaded = false;
    public readonly NATIONAL_BUSINESS_ENTITY_ID = 0;
    public businessEntities: IActiveIdAndNameDto[] = [];

    constructor(
        public readonly dialog: MatDialog,
        public readonly searchService: ProductCentreSearchService,
        public readonly businessEntitiesLogicService: BusinessEntitiesLogicService
    ) {
        super(dialog, searchService);
    }

    public ngOnInit(): void {
        this.searchService.productType = this.productType;
        this.searchService.categoryIds = [this.categoryId];
        this.searchService.businessEntityIdsToSortBy = [...this.businessEntityIds];
        this.searchService.ignoreTierBasedSorting = this.ignoreTierBasedSorting;
        this.businessEntitiesLogicService.getSkinnyBusinessEntityDetails().pipe(take(1)).subscribe(businessEntities => {
            this.businessEntities = businessEntities.filter(be => this.businessEntityIds.has(be.id));
        });
        super.ngOnInit();
    }

    public getPillColour(tier: ProductTierRangeEnumId): string {
        const pillColourDefault = '#607d8b';
        const pillColourFoundation = '#D38700';
        const pillColourTraditional = '#B3B3B3';
        const pillColourPremium = '#F6D500';

        switch (tier) {
            case ProductTierRangeEnumId.Foundation:
                return pillColourFoundation;
            case ProductTierRangeEnumId.Traditional:
                return pillColourTraditional;
            case ProductTierRangeEnumId.Premium:
                return pillColourPremium;
            default:
                return pillColourDefault;
        }
    }

    public tierMatchesBusinessEntity(productTier: IProductCentreTierDocumentDto): boolean {
        if (!this.businessEntityIds.size) {
            return true;
        }

        const nationalIsSelected = this.businessEntityIds.has(this.NATIONAL_BUSINESS_ENTITY_ID);
        if (nationalIsSelected && productTier.isNational) {
            return true;
        }

        return productTier.businessEntities.some(b => this.businessEntityIds.has(b));
    }

    public getDisplayText(item: any): string {
        const title: string[] = [];
        title.push(item.name || item.offering);
        if (item.code || item.offeringId) {
            title.push(item.code || item.offeringId);
        }
        return title.join(this.separator);
    }

    public getCategoryDisplay(item: any): string {
        if (item.categoryPath) {
            let terms = item.categoryPath;
            const firstCategory = item.categoryPath[0];
            if (firstCategory != null && firstCategory.label) {
                terms = terms.map(category => category.label);
            }
            const categoryList = terms != null && terms.length > 0 ? terms.join(' > ') : '';
            return `${categoryList} ${this.separator}`;
        }
        return '';
    }

    public getItemUom(item: any): string {
        return item.uomDisplay ? ` (${item.uomDisplay})` : '';
    }

    public displayWith = (item: any): string => {
        let displayText = '';
        if (item) {
            if (this.displayCategories) {
                displayText = this.getCategoryDisplay(item);
            }
            displayText += this.getDisplayText(item) + this.getItemUom(item);
        }
        return displayText;
    };

    public getOptionIsStandardProduct(option: any): boolean {
        return option.isStandardProduct;
    }

    protected readonly TIER_ENUM = PRODUCT_TIER_RANGE_ENUM;

    public getNamesForBusinessEntities(tier: IProductCentreTierDocumentDto): string {
        if (
            tier.isNational === false &&
            (tier.businessEntities?.length === 0 || this.businessEntities?.length === 0)
        ) {
            return null;
        }

        let result = '';

        if (tier.isNational) {
            result = result.concat('National, ');
        }

        tier.businessEntities.forEach(businessEntityId => {
            const match = this.businessEntities.find(be => be.id === businessEntityId);
            if (match) {
                result = result.concat(match.name + ', ');
            }
        });

        if (result !== '') {
            result = result.slice(0, result.length - 2);
        }

        return result;
    }
}
